import React, { useEffect, useRef, useState, useCallback } from 'react'
import { Play, Pause, RotateCcw } from 'lucide-react'

import { prepCanvas } from './canvasUtil'

interface Props { title?: string; description?: string; onComplete?: () => void }

// Mô tả & truyền sóng (Lớp 11 — Bài 5): sóng ngang chạy theo phương x;
// các phần tử dao động theo phương y. Chỉnh biên độ A, bước sóng λ, tần số f → quan sát v=λf, T=1/f.
const WavePropagationSimulation: React.FC<Props> = ({
  title = 'Mô tả và sự truyền sóng',
  description = 'Sóng ngang lan truyền: mỗi phần tử dao động tại chỗ, pha dao động truyền đi. Quan sát λ, T, v = λ·f.',
  onComplete,
}) => {
  const cv = useRef<HTMLCanvasElement>(null)
  const raf = useRef<number | null>(null)
  const tRef = useRef(0)
  const lastRef = useRef<number | null>(null)
  const [running, setRunning] = useState(true)
  const [A, setA] = useState(40)         // biên độ (px)
  const [lambda, setLambda] = useState(160) // bước sóng (px)
  const [f, setF] = useState(0.5)        // tần số (Hz)
  const firstRef = useRef(false)
  const W = 640, H = 240

  const draw = useCallback(() => {
    const ctx = prepCanvas(cv.current, W, H); if (!ctx) return
    ctx.clearRect(0, 0, W, H)
    // ô lưới (giúp đọc/đếm bước sóng λ)
    ctx.lineWidth = 1
    for (let gx = 0; gx <= W; gx += 40) { ctx.strokeStyle = gx % 80 === 0 ? '#dbe4ee' : '#eef2f7'; ctx.beginPath(); ctx.moveTo(gx, 0); ctx.lineTo(gx, H); ctx.stroke() }
    for (let gy = 0; gy <= H; gy += 40) { ctx.strokeStyle = '#eef2f7'; ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(W, gy); ctx.stroke() }
    const y0 = H / 2
    const k = (2 * Math.PI) / lambda
    const w = 2 * Math.PI * f
    const t = tRef.current
    // trục
    ctx.strokeStyle = '#e2e8f0'; ctx.lineWidth = 1
    ctx.beginPath(); ctx.moveTo(0, y0); ctx.lineTo(W, y0); ctx.stroke()
    // đường sóng y = A·sin(kx − ωt)
    ctx.strokeStyle = '#0ea5e9'; ctx.lineWidth = 2.5; ctx.beginPath()
    for (let x = 0; x <= W; x += 2) {
      const y = y0 - A * Math.sin(k * x - w * t)
      x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)
    }
    ctx.stroke()
    // phần tử nổi bật (dao động tại chỗ)
    for (let i = 0; i < W; i += Math.round(lambda / 4)) {
      const y = y0 - A * Math.sin(k * i - w * t)
      ctx.beginPath(); ctx.arc(i, y, 4, 0, Math.PI * 2); ctx.fillStyle = '#ef4444'; ctx.fill()
    }
    // đánh dấu 1 bước sóng λ
    ctx.strokeStyle = 'rgba(100,116,139,0.6)'; ctx.setLineDash([4, 3])
    ctx.beginPath(); ctx.moveTo(20, 24); ctx.lineTo(20 + lambda, 24); ctx.stroke(); ctx.setLineDash([])
    ctx.fillStyle = '#475569'; ctx.font = '12px sans-serif'; ctx.fillText('λ', 20 + lambda / 2 - 4, 18)
  }, [A, lambda, f])

  const tick = useCallback((ts: number) => {
    if (lastRef.current == null) lastRef.current = ts
    const dt = Math.min(0.05, (ts - lastRef.current) / 1000); lastRef.current = ts
    tRef.current += dt
    draw()
    raf.current = requestAnimationFrame(tick)
  }, [draw])

  useEffect(() => {
    if (running) { if (!firstRef.current) { firstRef.current = true; onComplete?.() } lastRef.current = null; raf.current = requestAnimationFrame(tick) }
    else if (raf.current) cancelAnimationFrame(raf.current)
    return () => { if (raf.current) cancelAnimationFrame(raf.current) }
  }, [running, tick, onComplete])
  useEffect(() => { draw() }, [draw])

  const v = (lambda * f).toFixed(0)
  const T = (1 / f).toFixed(2)

  return (
    <div className="rounded-2xl border border-sky-200 bg-white p-4 my-3">
      <h4 className="font-bold text-sky-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <canvas ref={cv} width={W} height={H} className="w-full rounded-lg border border-gray-100 bg-slate-50" />
      <div className="grid grid-cols-3 gap-2 my-3 text-center">
        <div className="p-2 rounded-lg bg-sky-50"><div className="text-lg font-black text-sky-700">{v} px/s</div><div className="text-[11px] text-gray-500">Tốc độ v = λ·f</div></div>
        <div className="p-2 rounded-lg bg-violet-50"><div className="text-lg font-black text-violet-700">{T} s</div><div className="text-[11px] text-gray-500">Chu kì T = 1/f</div></div>
        <div className="p-2 rounded-lg bg-amber-50"><div className="text-lg font-black text-amber-700">{lambda} px</div><div className="text-[11px] text-gray-500">Bước sóng λ</div></div>
      </div>
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm"><span className="w-28 text-gray-600">Biên độ A</span>
          <input type="range" min={10} max={80} value={A} onChange={(e) => setA(Number(e.target.value))} className="flex-1" /><span className="w-12 text-right font-semibold">{A}</span></label>
        <label className="flex items-center gap-2 text-sm"><span className="w-28 text-gray-600">Bước sóng λ</span>
          <input type="range" min={80} max={320} step={10} value={lambda} onChange={(e) => setLambda(Number(e.target.value))} className="flex-1" /><span className="w-12 text-right font-semibold">{lambda}</span></label>
        <label className="flex items-center gap-2 text-sm"><span className="w-28 text-gray-600">Tần số f</span>
          <input type="range" min={0.2} max={2} step={0.1} value={f} onChange={(e) => setF(Number(e.target.value))} className="flex-1" /><span className="w-12 text-right font-semibold">{f}Hz</span></label>
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={() => setRunning((r) => !r)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-sky-600 text-white font-bold hover:bg-sky-700">
          {running ? <><Pause className="w-4 h-4" /> Tạm dừng</> : <><Play className="w-4 h-4" /> Chạy</>}</button>
        <button onClick={() => { tRef.current = 0; draw() }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 text-gray-600 font-bold hover:bg-gray-50">
          <RotateCcw className="w-4 h-4" /> Đặt lại</button>
      </div>
    </div>
  )
}
export default WavePropagationSimulation
